import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { fmtRelative, fmtInt } from '../lib/format'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'

/**
 * One title's newsletter list.
 *
 * Pending rows are people who typed an address and never clicked the
 * confirmation link; they are not an audience and are never sent to. A
 * confirmed row with no synced-at is on no Mailgun list yet, which is
 * expected until the four lists exist.
 */

type SubscriberRow = {
  id: number
  email: string
  status: string
  source: string
  created_at: string | null
  confirmed_at: string | null
  mailgun_synced_at: string | null
}

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
]

const inputClass =
  'bg-ink-900 border border-ink-500 px-2.5 py-1.5 font-mono text-[12px] text-paper focus:outline-none focus:border-amber-glow/60'

export function NewsletterSubscribersPage() {
  const { pub = '' } = useParams()
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')

  const query = new URLSearchParams()
  if (status) query.set('status', status)
  if (q.trim()) query.set('q', q.trim())
  const qs = query.toString()

  const { data, isLoading } = useQuery({
    queryKey: ['newsletter-subscribers', pub, status, q],
    queryFn: () =>
      api.get<{ items: SubscriberRow[]; total: number }>(
        `/api/admin/newsletters/${pub}/subscribers${qs ? `?${qs}` : ''}`,
      ),
  })

  const unsubscribe = useMutation({
    mutationFn: (id: number) =>
      api.post(`/api/admin/newsletters/${pub}/subscribers/${id}/unsubscribe`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['newsletter-subscribers', pub] })
      qc.invalidateQueries({ queryKey: ['newsletters'] })
    },
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="mb-2"><PubBadge slug={pub} /></div>
          <h1 className="font-serif text-[36px] leading-[1.05] tracking-tight text-paper">
            Newsletter subscribers
          </h1>
        </div>
      </header>

      <Panel
        label="Filter"
        right={
          <a
            href={`/api/admin/newsletters/${pub}/subscribers.csv${qs ? `?${qs}` : ''}`}
            className="label text-paper-dim hover:text-amber-glow"
          >
            Export CSV
          </a>
        }
      >
        <div className="flex items-end gap-4">
          <div className="w-48">
            <label className="label block mb-1.5">Status</label>
            <select
              className={`${inputClass} w-full`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="label block mb-1.5">Search</label>
            <input
              className={`${inputClass} w-full`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search an address"
            />
          </div>
          <div className="label text-paper-dim pb-2">{fmtInt(data?.total ?? 0)} matching</div>
        </div>
      </Panel>

      {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}

      {!isLoading && data && (
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-[13px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="label py-2 px-4 font-normal text-left">Address</th>
                <th className="label py-2 px-3 font-normal text-left">Status</th>
                <th className="label py-2 px-3 font-normal text-left">Signed up</th>
                <th className="label py-2 px-3 font-normal text-left">Confirmed</th>
                <th className="label py-2 px-3 font-normal text-left">On the list</th>
                <th className="label py-2 px-3 font-normal text-left"></th>
              </tr>
            </thead>
            <tbody>
              {(data.items ?? []).map((r) => (
                <tr key={r.id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <td className="px-4 py-2 text-paper">{r.email}</td>
                  <td className="px-3 py-2 text-paper-dim">{r.status}</td>
                  <td className="px-3 py-2 text-paper-dim tabular-nums">
                    {r.created_at ? fmtRelative(r.created_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-paper-dim tabular-nums">
                    {r.confirmed_at ? fmtRelative(r.confirmed_at) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {r.status !== 'confirmed' ? (
                      <span className="text-paper-trace">—</span>
                    ) : r.mailgun_synced_at ? (
                      <span className="text-radar-green">Yes</span>
                    ) : (
                      <span className="text-amber-glow">Not yet</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.status !== 'unsubscribed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={unsubscribe.isPending}
                        onClick={() => unsubscribe.mutate(r.id)}
                      >
                        Unsubscribe
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="label text-paper-trace p-6 text-center">
                    No subscribers match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
